import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "items center flex flex-wrap gap-4" }

import { CardMenu } from '@/components/ui/card';
    import { createVoucherColumns } from '@/tables/columns/vouchers';
    import { computed } from 'vue';
    import { Head } from '@inertiajs/vue3';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import type { SharedData, BreadcrumbItem, NavItem, Voucher } from '@/types';
    import MissingVouchers from '@/components/vouchers/MissingVouchers.vue';
    import VoucherBulkActions from '@/components/vouchers/VoucherBulkActions.vue';
    import VoucherClosePeriod from '@/components/vouchers/VoucherClosePeriod.vue';
    import VoucherApproveAll from '@/components/vouchers/VoucherApproveAll.vue';
    import VoucherReleaseAll from '@/components/vouchers/VoucherReleaseAll.vue';
    import VoucherStatementUpload from '@/components/vouchers/VoucherStatementUpload.vue';
    import VoucherClearList from '@/components/vouchers/VoucherClearList.vue';
    import VoucherAdministrate from '@/components/vouchers/VoucherAdministrate.vue';
    import QueryKey from '@/enums/QueryKey';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'Voucher',
  setup(__props) {

    const { hasPermission, hasAnyPermission } = usePermissions();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);
    
    const { data: economicDimensions } = useQuery({
        queryKey: ['economic-dimensions', selectedCompanyId],
        queryFn: async () => {
            if (!selectedCompanyId.value) return [];
            const { data } = await axios.get(route('v2.voucher.dimensions.get', selectedCompanyId.value));
            return data || [];
        },
        enabled: computed(() => !!selectedCompanyId.value),
        placeholderData: [],
        retry: 1,
        refetchOnMount: true,
    });
    
    const columns = computed(() => createVoucherColumns(economicDimensions.value || []));

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'vouchers.page_header',
        },
    ];

    const navItems: NavItem[] = [
        {
            title: 'vouchers.current_vouchers.header',
            href: route('v2.vouchers.index'),
            permissionLevel: Permission.VOUCHERS_VIEW || Permission.VOUCHERS_OWN_VIEW,
        },
        {
            title: 'vouchers.earlier_releases.header',
            href: route('v2.vouchers.earlier-releases.index'),
            permissionLevel: Permission.VOUCHERS_EARLIER_RELEASES_VIEW,
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.VOUCHERS, selectedCompanyId],
        queryFn: getVouchers,
    });

    async function getVouchers() {
        let vouchersRoute = '';
        if (
            hasPermission(Permission.VOUCHERS_OWN_VIEW) &&
            !hasPermission(Permission.VOUCHERS_VIEW)
        ) {
            vouchersRoute = 'v2.vouchers.own.table';
        } else {
            vouchersRoute = 'v2.vouchers.table';
        }

        const { data } = await axios.get(route(vouchersRoute, selectedCompanyId.value));

        return data.data;
    }

    const missingVouchers = computed(() => {
        if (!data.value) return 0;
        return data.value.filter((voucher: Voucher) => voucher.uploaded === 0).length;
    });

    const totalVouchers = computed(() => {
        return data.value?.length || 0;
    });

    const hasMissingVouchers = computed(() => {
        return data.value && missingVouchers.value > 0;
    });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Head), {
      title: _ctx.$t('vouchers.page_header')
    }, null, 8, ["title"]),
    _createVNode(AppLayout, {
      breadcrumbs: breadcrumbs,
      "require-selected-company": ""
    }, {
      default: _withCtx(() => [
        _createVNode(PageCard, null, {
          default: _withCtx(() => [
            _createVNode(DataTable, {
              "table-key": _unref(QueryKey).VOUCHERS,
              columns: columns.value,
              data: _unref(data),
              loading: _unref(isPending),
              hideControls: true,
              manualPagination: true,
              "default-sorting": [
                    {
                        id: 'Date',
                        desc: true,
                    },
                ]
            }, {
              title: _withCtx(() => [
                _createVNode(_unref(CardMenu), { items: navItems }),
                _createVNode(MissingVouchers, {
                  "missing-vouchers": missingVouchers.value,
                  "total-vouchers": totalVouchers.value
                }, null, 8, ["missing-vouchers", "total-vouchers"])
              ]),
              "table-top": _withCtx(({ table }) => [
                _createElementVNode("div", _hoisted_1, [
                  (
                                _unref(hasPermission)(_unref(Permission).VOUCHERS_ADMIN_UPDATE) &&
                                !_unref(isPending) &&
                                !hasMissingVouchers.value
                            )
                    ? (_openBlock(), _createBlock(VoucherClosePeriod, { key: 0 }))
                    : _createCommentVNode("", true),
                  (_unref(hasPermission)(_unref(Permission).VOUCHERS_UPDATE))
                    ? (_openBlock(), _createBlock(VoucherApproveAll, { key: 1 }))
                    : _createCommentVNode("", true),
                  (_unref(hasPermission)(_unref(Permission).VOUCHERS_ADMIN_CREATE))
                    ? (_openBlock(), _createBlock(VoucherReleaseAll, { key: 2 }))
                    : _createCommentVNode("", true),
                  (_unref(hasPermission)(_unref(Permission).VOUCHERS_ADMIN_UPDATE))
                    ? (_openBlock(), _createBlock(VoucherStatementUpload, { key: 3 }))
                    : _createCommentVNode("", true),
                  (_unref(hasPermission)(_unref(Permission).VOUCHERS_ADMIN_DELETE))
                    ? (_openBlock(), _createBlock(VoucherClearList, { key: 4 }))
                    : _createCommentVNode("", true),
                  (_unref(hasPermission)(_unref(Permission).VOUCHERS_ADMIN_UPDATE))
                    ? (_openBlock(), _createBlock(VoucherAdministrate, { key: 5 }))
                    : _createCommentVNode("", true),
                  (
                                _unref(hasAnyPermission)([
                                    _unref(Permission).VOUCHERS_ADMIN_UPDATE,
                                    _unref(Permission).VOUCHERS_ADMIN_DELETE,
                                ])
                            )
                    ? (_openBlock(), _createBlock(VoucherBulkActions, {
                        key: 6,
                        table: table
                      }, null, 8, ["table"]))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            }, 8, ["table-key", "columns", "data", "loading"])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 64))
}
}

})